import { compact, round as _round, every, sortBy } from 'lodash';
import { detailedDiff } from 'deep-object-diff';
import { Image } from '../model/asset';

type PresenceFunctionInput = number | string | object;

export function present<T extends PresenceFunctionInput>(value?: T | null): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'number') {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  return false;
}

export function presence<T extends PresenceFunctionInput>(value?: T | null): T | undefined {
  return present(value) ? (value as T) : undefined;
}

export function notNil<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

/**
 * Invokes the callback only if the value is present.
 * @param value potentially blank value
 * @param callback function to call when value is present
 */
export function tapPresent<T extends PresenceFunctionInput>(
  value: T | null | undefined,
  callback: (value: T) => void
): void {
  const presentValue = presence(value);
  presentValue && callback(presentValue);
}

export function coalesce<T extends number>(value: T | null | undefined, defaultValue: T): T {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return value as T;
}

export function joinJsonValues(value: string): string {
  const json = JSON.parse(value);
  const values: string[] = Object.values(json);
  return values
    .filter((val) => {
      if (new Date(val).toString() !== 'Invalid Date') {
        return false;
      }
      if (typeof val === 'string') {
        return true;
      }
      return false;
    })
    .join(' - ');
}

export function objectToParamsPart(object: Record<string, string | number | null | undefined>): string {
  const entries = Object.entries(object).map(([key, value]) =>
    value !== null && value !== undefined ? `${key}=${value}` : null
  );
  const queryPart = compact(entries).join('&');
  return queryPart.length === 0 ? '' : `?${queryPart}`;
}

export function objectToOptions(object: Record<string, string>): Array<{ label: string; value: string }> {
  return Object.entries(object).map(([value, label]) => ({ label, value }));
}

export function applyTax(base: number, taxRate: number): number {
  return _round(base * (1 + taxRate), 2);
}

export function removeTax(gross: number, taxRate: number): number {
  return gross / (1 + taxRate);
}

export function NaNGuard(number: number): number {
  return Number.isNaN(number) ? 0 : number;
}

// Returns only the image hash removing the image type info (i.E: data:image/png;base64);
export function getImageHash(image?: string | null): string | null {
  if (!image) {
    return null;
  }

  return image.split(',')[1];
}

export const oneLevelDiff = (
  obj: Image[],
  obj2: Image[]
): {
  edited: Image[];
  deleted: string[];
} => {
  const edited = obj2.filter((item2, index2: number) =>
    obj.find((item, index: number) => {
      return (
        item.id === item2.id &&
        (index !== index2 || item.tags.length !== item2.tags.length || item.order !== item2.order)
      );
    })
  );
  const deleted: string[] = obj.filter((item) => !obj2.find((item2) => item.id === item2.id)).map((item) => item.id);
  return {
    edited,
    deleted,
  };
};

/* Turns 6b to 6.98 for proper sort and interpolation, appends the ascii of the characters following the string */
export function numerify(input: string): number {
  const regex = new RegExp(/([0-9]+)(\w*)/);
  const groups = regex.exec(input);
  if (groups) {
    if (groups[2]) {
      return parseFloat(
        groups[1] +
          '.' +
          groups[2]
            .split('')
            .map((str) => {
              return str.charCodeAt(0);
            })
            .join('')
      );
    } else {
      return parseInt(groups[1], 10);
    }
  }
  return 0;
}

export function round(s: string | number, precision: number): number {
  return +Number(s).toFixed(precision);
}

export const isNumber = (content: string): boolean => {
  return parseInt(content, 10).toString() === content;
};

export const sortNumerically = (elements: string[]): string[] => {
  if (every(elements, isNumber)) {
    return sortBy(elements, parseInt);
  } else {
    return elements.sort();
  }
};

export function compareNullity<T>(a: T | null | undefined, b: T | null | undefined, nullsFirst = true): number {
  if (!a) {
    if (!b) {
      return 0;
    } else {
      return nullsFirst ? -1 : 1;
    }
  }
  if (!b) {
    return nullsFirst ? 1 : -1;
  }
  return 0;
}

export function comparePotentialNumbers(a: string | null | undefined, b: string | null | undefined): number {
  const nullComparison = compareNullity(a, b);
  if (nullComparison !== 0) return nullComparison;
  if (!a || !b) return 0; // to please the typesystem (if nullComparison == 0, then neither are null).

  const aAsNumber = parseFloat(a);
  if (aAsNumber.toString() === a) {
    const bAsNumber = parseFloat(b);
    if (bAsNumber.toString() === b) {
      return aAsNumber - bAsNumber;
    }
  }

  return a?.localeCompare(b);
}

interface DetailedDiff {
  added: object;
  deleted: object;
  updated: object;
}

export function deepDiff(obj1: object, obj2: object): object {
  const diffs = detailedDiff(obj1, obj2) as DetailedDiff;
  return { ...diffs.added, ...diffs.updated };
}

// Result can be any-thing. If it its a property deep withing the object is hard to infer what type it is.
// eslint-disable-next-line
export function dig<T, K extends keyof T>(obj?: T, ...args: K[]): any {
  let result: T | T[K] | undefined = obj;

  const keysCount = args.length;
  for (let i = 0; i < keysCount; i += 1) {
    if (result === null || result === undefined) {
      return result;
    }

    result = (result as T)[args[i]];
  }

  return result;
}
